/**
 * ETQ product page shop components, rebuilt from the live theme (inventory/live/assets/product.js):
 * - sticky buy bar (snippets/etq-sticky-atc): shows once the main buy buttons have scrolled out of
 *   view, hides when the footer comes in. The size button opens the size sheet; "Add to bag" clicks
 *   Horizon's main add button, or asks for the size first and adds right after the pick (live data-direct)
 * - "Choose size" sheet (snippets/etq-size-sheet): a pick clicks the matching tile in Horizon's main
 *   picker, so Horizon updates price, URL and add-to-bag itself; a sold-out size shows the email field under
 *   its row (event etq:bis-open, handled by assets/etq-bis.js; no second sheet); "View size guide" opens
 *   the size guide drawer on top (no second dim, as live)
 * - "Added to shopping bag" sheet (snippets/etq-added-sheet): opens after a successful add from the
 *   main product form, on Horizon's cart lines update event (@shopify/events, action "add"); the
 *   bought-together cards add through /cart/add.js and announce it with the same event, so the bag
 *   count and the cart drawer stay in sync
 * - all sheets: drag the handle down to close on mobile (live popup__handle behaviour)
 *
 * Opening and closing reuse assets/etq.js: [data-etq-drawer-open="id"] opens <dialog id>, and
 * [data-etq-drawer-close] or a click on the dim closes any dialog.etq-drawer.
 */

const DESKTOP = window.matchMedia('(min-width: 1024px)');

const sizeSheet = /** @type {HTMLDialogElement | null} */ (document.getElementById('etq-size-sheet'));
const addedSheet = /** @type {HTMLDialogElement | null} */ (document.getElementById('etq-added-sheet'));
const fbtSheet = /** @type {HTMLDialogElement | null} */ (document.getElementById('etq-fbt-size'));
const sticky = /** @type {HTMLElement | null} */ (document.querySelector('[data-etq-sticky-atc]'));
const productId = (sticky ?? sizeSheet ?? addedSheet)?.dataset.productId ?? '';

// Horizon's standard events (cart lines update). Loaded through the theme's import map; if it ever
// fails, the sticky bar and size sheet still work and Horizon's own add flow stays untouched.
const eventsModule = import('@shopify/events').catch(() => null);

/* ---------- Horizon's main product form and picker ---------- */

/** @returns {HTMLElement | null} The product form of the page's own product (not quick add or cards) */
function mainForm() {
  for (const form of document.querySelectorAll('product-form-component')) {
    if (!(form instanceof HTMLElement) || form.dataset.productId !== productId) continue;
    if (form.closest('dialog, product-card, sticky-add-to-cart, quick-add-component')) continue;
    return form;
  }
  return null;
}

/** @returns {HTMLButtonElement | null} */
function mainAddButton() {
  const button = mainForm()?.querySelector('button[name="add"]');
  return button instanceof HTMLButtonElement ? button : null;
}

function mainPicker() {
  return document.querySelector('variant-picker[data-template-product-match]');
}

/** @returns {HTMLButtonElement[]} The size rows of the size sheet */
function sizeRows() {
  return sizeSheet ? Array.from(sizeSheet.querySelectorAll('button[data-etq-size-value]')) : [];
}

/** @param {HTMLElement} row */
function mainInputFor(row) {
  const input = mainPicker()?.querySelector(`input[data-option-value-id="${row.dataset.etqSizeValue}"]`);
  return input instanceof HTMLInputElement ? input : null;
}

/** @returns {HTMLButtonElement | null} The size row that matches the size chosen in the main picker */
function chosenRow() {
  return sizeRows().find((row) => mainInputFor(row)?.checked) ?? null;
}

/* ---------- Sheets (native <dialog>) ---------- */

/**
 * @param {HTMLDialogElement | null} dialog
 * @param {{ intent?: string, nested?: boolean }} [options]
 */
function openSheet(dialog, { intent = '', nested = false } = {}) {
  if (!dialog) return;
  dialog.dataset.intent = intent;
  // A sheet opened from inside another one gets no second dim (live data-overlay-hidden)
  dialog.toggleAttribute('data-etq-nested', nested);
  if (!dialog.open) dialog.showModal();
}

for (const dialog of [sizeSheet, addedSheet, fbtSheet]) {
  dialog?.addEventListener('close', () => {
    dialog.dataset.intent = '';
    // After the closing transition, so a sheet that was dragged away does not flash back first
    window.setTimeout(() => {
      if (dialog.open) return;
      dialog.removeAttribute('data-etq-nested');
      dialog.style.removeProperty('transform');
      dialog.style.removeProperty('transition');
    }, 600);
  });
}

/**
 * Mobile drag handle (live popup__handle): the sheet follows the finger downwards; on release it
 * closes when the last movement went down, else it springs back.
 * @param {HTMLDialogElement | null} dialog
 */
function enableDrag(dialog) {
  const head = dialog?.querySelector('.etq-sheet__head');
  if (!dialog || !head) return;
  let startY = 0;
  let lastY = 0;
  let previousY = 0;
  let dragging = false;

  head.addEventListener(
    'touchstart',
    (event) => {
      if (DESKTOP.matches || !('touches' in event)) return;
      dragging = true;
      startY = lastY = previousY = event.touches[0]?.clientY ?? 0;
      dialog.style.transition = 'none';
    },
    { passive: true }
  );

  head.addEventListener(
    'touchmove',
    (event) => {
      if (!dragging || !('touches' in event)) return;
      previousY = lastY;
      lastY = event.touches[0]?.clientY ?? lastY;
      dialog.style.transform = `translateY(${Math.max(0, lastY - startY)}px)`;
    },
    { passive: true }
  );

  const release = () => {
    if (!dragging) return;
    dragging = false;
    dialog.style.transition =
      'transform 0.4s var(--etq-ease), overlay 0.5s allow-discrete, display 0.5s allow-discrete';
    if (lastY - startY > 24 && lastY >= previousY) {
      dialog.style.transform = 'translateY(100%)';
      window.setTimeout(() => dialog.close(), 300);
    } else {
      dialog.style.transform = '';
      window.setTimeout(() => dialog.style.removeProperty('transition'), 700);
    }
  };
  head.addEventListener('touchend', release);
  head.addEventListener('touchcancel', release);
}

[sizeSheet, addedSheet, fbtSheet].forEach(enableDrag);

/* ---------- Size sheet ---------- */

const stickySize = sticky?.querySelector('[data-etq-sticky-size]');
const stickyAdd = sticky?.querySelector('[data-etq-sticky-add]');
const selectSizeText = stickySize?.textContent ?? '';

/** Reflects the main picker's size in the sheet (selected row) and the sticky bar (button label) */
function syncSize() {
  const row = chosenRow();
  for (const other of sizeRows()) other.setAttribute('aria-pressed', String(other === row));

  if (stickySize) {
    stickySize.textContent = row ? `${sticky?.dataset.sizeWord}: ${row.dataset.etqSizeLabel}` : selectSizeText;
  }
  sticky?.toggleAttribute('data-size-chosen', Boolean(row));

  const soldOut =
    sticky?.hasAttribute('data-product-sold-out') || (row ? row.getAttribute('aria-disabled') === 'true' : false);
  stickyAdd?.toggleAttribute('data-sold-out', Boolean(soldOut));
}

/** @param {HTMLButtonElement} row */
function pickSize(row) {
  // Sold out: show the email field under this row instead of choosing it (assets/etq-bis.js)
  if (row.getAttribute('aria-disabled') === 'true') {
    openBackInStock(row.dataset.etqVariantId, Boolean(sizeSheet?.open));
    return;
  }

  const input = mainInputFor(row);
  const addAfterPick = sizeSheet?.dataset.intent === 'add';

  // The real tile click: Horizon's variant picker listens for this change and updates price, URL
  // and the add-to-bag button; assets/etq.js lifts its size guard on the same change
  if (input && !input.checked) input.click();
  syncSize();
  sizeSheet?.close();

  // Opened from "Add to bag" without a size: add right after the pick, as live does. Horizon queues
  // the add until its variant update is in, so the chosen size is the one that is added.
  if (addAfterPick && input) {
    window.requestAnimationFrame(() => mainAddButton()?.click());
  }
}

/**
 * Back in stock for a sold-out size (assets/etq-bis.js listens): inSheet → the email field opens under
 * that row of the size sheet, else the field at the add button is scrolled to and focused
 * @param {string | undefined} variantId
 * @param {boolean} inSheet - Asked from the size sheet
 */
function openBackInStock(variantId, inSheet) {
  if (!variantId) return;
  document.dispatchEvent(new CustomEvent('etq:bis-open', { detail: { variantId, inSheet } }));
}

function openSizeGuide() {
  const guide = document.querySelector('dialog.etq-drawer[id^="etq-size-guide-"]');
  if (!(guide instanceof HTMLDialogElement)) return;
  openSheet(guide, { nested: true });
  guide.addEventListener('close', () => guide.removeAttribute('data-etq-nested'), { once: true });
}

document.addEventListener('change', (event) => {
  if (event.target instanceof HTMLInputElement && event.target.closest('variant-picker[data-template-product-match]')) {
    syncSize();
  }
});

// "Add to bag" without a size (assets/etq.js): choose in the sheet, the add follows the pick
document.addEventListener('etq:size-sheet-add', () => openSheet(sizeSheet, { intent: 'add' }));

/* ---------- Sticky buy bar ---------- */

function onStickyAdd() {
  const button = mainAddButton();
  if (!sizeRows().length) {
    button?.click();
    return;
  }
  const row = chosenRow();
  // A sold-out size: "Notify me" goes to the email field at the add button
  if (row?.getAttribute('aria-disabled') === 'true') {
    openBackInStock(row.dataset.etqVariantId, false);
    return;
  }
  if (!row || !button || button.disabled) {
    openSheet(sizeSheet, { intent: 'add' });
    return;
  }
  button.click();
}

function setupSticky() {
  if (!sticky) return;
  // Out of the section tree, so no transformed or contained ancestor can break position: fixed
  document.body.append(sticky);

  // The buy-buttons form (a stable block box; Horizon only morphs what is inside it)
  const watched = mainForm()?.querySelector('form') ?? mainForm();
  const footer = document.querySelector('footer');
  let buyButtonsAbove = false;
  let footerInView = false;

  const update = () => {
    const show = buyButtonsAbove && !footerInView;
    sticky.toggleAttribute('data-visible', show);
    sticky.inert = !show;
  };

  if (watched) {
    new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      // Only once the buttons are above the screen, not while they are still below it
      buyButtonsAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      update();
    }).observe(watched);
  }
  if (footer) {
    new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      footerInView = entry.isIntersecting;
      update();
    }).observe(footer);
  }
  update();
}

/* ---------- Added-to-bag sheet ---------- */

/** @type {Record<string, {size: string, price: string}>} */
let addedVariants = {};
try {
  addedVariants = JSON.parse(addedSheet?.querySelector('[data-etq-added-variants]')?.textContent || '{}');
} catch {
  addedVariants = {};
}

/* ===== Added sheet calm (1 Nov) ===== */
/**
 * "Order before 14:00. Shipped the same day." only holds on a shipping day before the cut-off; otherwise
 * the line says "Shipped the next working day." Same clock as the PDP countdown in assets/etq.js:
 * Amsterdam time, no weekends, closed dates, Friday cut-off.
 */
function syncAddedDelivery() {
  const line = addedSheet?.querySelector('[data-etq-added-delivery]');
  if (!(line instanceof HTMLElement)) return;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (/** @type {string} */ type) => parts.find((part) => part.type === type)?.value ?? '';
  const weekday = get('weekday');
  const cutoff = (weekday === 'Fri' && line.dataset.cutoffFriday) || line.dataset.cutoff || '';
  const [hours = 0, minutes = 0] = cutoff.split(':').map(Number);
  const closed = (line.dataset.closed || '').split(',');
  const sameDay =
    !['Sat', 'Sun'].includes(weekday) &&
    !closed.includes(`${get('year')}-${get('month')}-${get('day')}`) &&
    Number(get('hour')) * 60 + Number(get('minute')) < hours * 60 + minutes;
  line.textContent = (sameDay ? line.dataset.etqSameDay : line.dataset.etqNext) ?? '';
}

/* Bought-together carousel: swipe on a phone; on desktop scroll or arrows (shown only when the cards overflow) */
const fbtCards = addedSheet?.querySelector('[data-etq-fbt-list-cards]');
const fbtArrows = addedSheet?.querySelector('[data-etq-fbt-arrows]');

function syncFbtArrows() {
  if (!(fbtCards instanceof HTMLElement) || !(fbtArrows instanceof HTMLElement)) return;
  const max = fbtCards.scrollWidth - fbtCards.clientWidth;
  fbtArrows.hidden = max <= 1;
  for (const arrow of fbtArrows.querySelectorAll('button')) {
    arrow.disabled = arrow.dataset.etqFbtStep === '-1' ? fbtCards.scrollLeft <= 1 : fbtCards.scrollLeft >= max - 1;
  }
}

fbtCards?.addEventListener('scroll', syncFbtArrows, { passive: true });
document.addEventListener('scroll', (event) => {
  const list = event.target;
  if (!(list instanceof Element) || !list.matches('[data-etq-fbt-mini-list]')) return;
  const max = list.scrollWidth - list.clientWidth - 1;
  for (const arrow of list.closest('.etq-fbt-mini')?.querySelectorAll('[data-etq-fbt-mini-step]') ?? []) {
    arrow.disabled = arrow.dataset.etqFbtMiniStep === '-1' ? list.scrollLeft <= 1 : list.scrollLeft >= max;
  }
}, { capture: true, passive: true });
window.addEventListener('resize', syncFbtArrows);

// Read more only where the text really runs over. A closed accordion has no height: measured when it opens.
const syncClamps = (root = document) => {
  for (const clamp of root.querySelectorAll('[data-etq-clamp]:not(.is-open)')) {
    const text = clamp.querySelector('[data-etq-clamp-text]');
    const toggle = clamp.querySelector('[data-etq-clamp-toggle]');
    if (!text?.clientHeight || !toggle) continue;
    toggle.hidden = text.scrollHeight <= text.clientHeight + 2;
  }
};
syncClamps();
window.addEventListener('load', () => syncClamps());
window.addEventListener('resize', () => syncClamps());
document.addEventListener('toggle', (event) => {
  if (event.target instanceof HTMLDetailsElement && event.target.open) syncClamps(event.target);
}, true);
fbtArrows?.addEventListener('click', (event) => {
  const arrow = event.target instanceof Element ? event.target.closest('[data-etq-fbt-step]') : null;
  const card = fbtCards?.querySelector('.etq-added-sheet__card');
  if (!(arrow instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
  fbtCards?.scrollBy({ left: Number(arrow.dataset.etqFbtStep) * (card.offsetWidth + 10) });
});
/* ===== end Added sheet calm ===== */

/**
 * @param {string} variantId
 * @param {number} quantity
 */
function showAdded(variantId, quantity) {
  if (!addedSheet) return;
  const data = addedVariants[variantId];
  const qty = addedSheet.querySelector('[data-etq-added-qty]');
  const size = addedSheet.querySelector('[data-etq-added-size]');
  const price = addedSheet.querySelector('[data-etq-added-price]');
  if (qty) qty.textContent = String(quantity);
  if (size) size.textContent = data?.size ?? '';
  if (price && data) price.innerHTML = data.price;
  for (const extra of addedSheet.querySelectorAll('[data-etq-added-extra]')) extra.remove();
  syncAddedDelivery();

  // Fresh cards for every new add
  for (const button of addedSheet.querySelectorAll('[data-etq-fbt-select], [data-etq-fbt-add]')) {
    resetCardButton(/** @type {HTMLButtonElement} */ (button));
  }

  for (const dialog of [sizeSheet, fbtSheet]) if (dialog?.open) dialog.close();
  openSheet(addedSheet);
  fbtCards?.scrollTo({ left: 0, behavior: 'instant' });
  syncFbtArrows();
}

async function setupAddedSheet() {
  const events = await eventsModule;
  const eventName = events?.StandardEvents?.cartLinesUpdate;
  if (!addedSheet || !eventName) return;

  document.addEventListener(eventName, (event) => {
    const update = /** @type {any} */ (event);
    if (update.action !== 'add') return;
    const source = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!source || source !== mainForm()) return;

    // Added sheet calm (1 Nov): fetch the sheet's images while the add runs, so it opens without grey blocks
    for (const image of addedSheet.querySelectorAll('img[loading="lazy"]')) image.setAttribute('loading', 'eager');

    const line = update.lines?.[0];
    const variantId = String(line?.merchandiseId ?? source.querySelector('input[name="id"]')?.value ?? '')
      .split('/')
      .pop();

    update.promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        const quantity = Number(line?.quantity) || Number(result?.detail?.itemCount) || 1;
        showAdded(variantId ?? '', quantity);
      })
      .catch(() => {});
  });
}

/* ---------- Bought-together cards ---------- */

/** @type {HTMLButtonElement | null} */
let fbtSource = null;

/** @param {HTMLButtonElement} button */
function resetCardButton(button) {
  const label = button.querySelector('span');
  if (!label) return;
  label.dataset.text ??= label.textContent ?? '';
  label.textContent = label.dataset.text;
  button.disabled = false;
  button.removeAttribute('data-etq-done');
}

/** @param {HTMLButtonElement} button */
function openFbtSizes(button) {
  const list = fbtSheet?.querySelector('[data-etq-fbt-list]');
  if (!fbtSheet || !list) return;

  /** @type {{id: number, label: string, available: boolean}[]} */
  let sizes = [];
  try {
    sizes = JSON.parse(button.dataset.etqFbtSizes || '[]');
  } catch {
    sizes = [];
  }
  const soldOutText = fbtSheet.querySelector('[data-etq-sold-out-text]')?.textContent?.trim() ?? '';

  list.replaceChildren(
    ...sizes.map((size) => {
      const item = document.createElement('li');
      item.className = 'etq-size-sheet__item';
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'etq-size-sheet__size';
      row.dataset.etqFbtVariant = String(size.id);
      if (!size.available) row.setAttribute('aria-disabled', 'true');
      const label = document.createElement('span');
      label.className = 'etq-size-sheet__label';
      label.textContent = size.label;
      row.append(label);
      if (!size.available) {
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = `, ${soldOutText}`;
        row.append(hidden);
      }
      item.append(row);
      return item;
    })
  );

  const productLine = fbtSheet.querySelector('[data-etq-fbt-product]');
  if (productLine) productLine.textContent = button.dataset.etqFbtTitle ?? '';
  fbtSource = button;
  // On top of the added sheet no second dim; from the page itself the normal one
  openSheet(fbtSheet, { nested: Boolean(addedSheet?.open) });
}

/**
 * Adds one item to the bag and tells the rest of the theme, the way Horizon's product form does:
 * a cart lines update event whose promise resolves with the new cart (bag count, cart drawer).
 * @param {string} variantId
 * @returns {Promise<boolean>}
 */
async function addToBag(variantId) {
  if (!addedSheet) return false;
  const events = await eventsModule;
  const UpdateEvent = events?.CartLinesUpdateEvent;
  const deferred = UpdateEvent?.createPromise?.();
  const cartUrl = addedSheet.dataset.cartUrl || '/cart';

  if (UpdateEvent && deferred) {
    addedSheet.dispatchEvent(
      new UpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );
  }

  const fetchCart = () =>
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
      (response) => response.json()
    );

  const sections = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');

  try {
    const response = await fetch(addedSheet.dataset.cartAddUrl || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.message || 'Add to bag failed');

    const cart = await fetchCart();
    deferred?.resolve({
      cart: UpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections: result.sections, itemCount: 1, source: 'etq-added-sheet', didError: false },
    });
    return true;
  } catch (error) {
    // Resolve with the real cart so the bag count stays right, flagged as an error
    fetchCart()
      .then((cart) =>
        deferred?.resolve({
          cart: UpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: 0, source: 'etq-added-sheet', didError: true },
        })
      )
      .catch(() => deferred?.reject(error));
    return false;
  }
}

// The Shop the look sheet adds the same way (assets/etq-looks.js)
window.etqAddToBag = addToBag;

/**
 * Shows a bought-together item as its own line under the first product, so the sheet lists what was added
 * (Melvin 1 Nov: it did go in the bag, but nothing changed up here).
 * @param {HTMLButtonElement} button - The card's button
 * @param {string} sizeLabel
 */
function showAddedCard(button, sizeLabel) {
  const first = addedSheet?.querySelector('.etq-added-sheet__line:not([data-etq-added-extra])');
  const card = button.closest('.etq-added-sheet__card');
  if (!first || !card) return;
  const line = /** @type {HTMLElement} */ (first.cloneNode(true));
  line.setAttribute('data-etq-added-extra', '');
  line.querySelector('[data-etq-added-components]')?.remove();

  const image = line.querySelector('img');
  const cardImage = card.querySelector('img');
  if (image && cardImage) {
    image.src = cardImage.currentSrc || cardImage.src;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
  }
  const name = line.querySelector('.etq-added-sheet__name');
  if (name) name.textContent = `1x ${card.querySelector('.etq-added-sheet__card-title')?.textContent?.trim() ?? ''}`;
  const size = line.querySelector('.etq-added-sheet__size');
  if (size) size.textContent = sizeLabel;
  const price = line.querySelector('.etq-added-sheet__price');
  if (price) price.innerHTML = card.querySelector('.etq-added-sheet__card-price')?.innerHTML ?? '';
  for (const element of line.querySelectorAll('[data-etq-added-qty], [data-etq-added-size], [data-etq-added-price]')) {
    element.removeAttribute('data-etq-added-qty');
    element.removeAttribute('data-etq-added-size');
    element.removeAttribute('data-etq-added-price');
  }

  const lines = addedSheet?.querySelectorAll('.etq-added-sheet__line');
  lines?.[lines.length - 1]?.after(line);
  line.closest('.etq-added-sheet__body')?.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * @param {HTMLButtonElement} button - The card's button
 * @param {string} variantId
 * @param {string} [sizeLabel]
 */
async function addCard(button, variantId, sizeLabel = '') {
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const added = await addToBag(variantId);
  button.removeAttribute('aria-busy');
  if (added) {
    showAddedCard(button, sizeLabel);
    const label = button.querySelector('span');
    if (label) {
      label.dataset.text ??= label.textContent ?? '';
      label.textContent = button.dataset.etqAddedText ?? '';
    }
    button.setAttribute('data-etq-done', '');
  } else {
    button.disabled = false;
  }
}

/* ---------- Clicks ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  if (target.closest('[data-etq-sticky-add]')) {
    onStickyAdd();
    return;
  }

  const sizeRow = target.closest('button[data-etq-size-value]');
  if (sizeRow instanceof HTMLButtonElement && sizeSheet?.contains(sizeRow)) {
    pickSize(sizeRow);
    return;
  }

  const clampToggle = target.closest('[data-etq-clamp-toggle]');
  if (clampToggle) {
    clampToggle.closest('[data-etq-clamp]')?.classList.add('is-open');
    return;
  }

  const fbtMiniStep = target.closest('[data-etq-fbt-mini-step]');
  if (fbtMiniStep instanceof HTMLElement) {
    const list = fbtMiniStep.closest('.etq-fbt-mini')?.querySelector('[data-etq-fbt-mini-list]');
    list?.scrollBy({ left: Number(fbtMiniStep.dataset.etqFbtMiniStep) * (list.clientWidth + 8), behavior: 'smooth' });
    return;
  }

  if (target.closest('[data-etq-size-doubt]')) {
    const guide = document.querySelector('dialog.etq-drawer[id^="etq-size-guide-"]');
    if (guide instanceof HTMLDialogElement) {
      event.preventDefault();
      openSheet(guide);
    }
    return;
  }

  if (target.closest('[data-etq-size-guide]')) {
    openSizeGuide();
    return;
  }

  const select = target.closest('[data-etq-fbt-select]');
  if (select instanceof HTMLButtonElement) {
    openFbtSizes(select);
    return;
  }

  const single = target.closest('[data-etq-fbt-add]');
  if (single instanceof HTMLButtonElement) {
    addCard(single, single.dataset.etqFbtAdd ?? '');
    return;
  }

  const fbtRow = target.closest('[data-etq-fbt-variant]');
  if (fbtRow instanceof HTMLButtonElement && fbtRow.getAttribute('aria-disabled') !== 'true' && fbtSource) {
    const source = fbtSource;
    fbtSheet?.close();
    addCard(source, fbtRow.dataset.etqFbtVariant ?? '', fbtRow.querySelector('.etq-size-sheet__label')?.textContent?.trim() ?? '');
  }
});

/* ---------- Gallery photos ---------- */

/**
 * From place 3 the gallery photos are lazy (snippets/product-media-gallery-content), so they don't hold up the
 * first screen. They come in shortly after load, or at the first touch, so a swipe never lands on an empty slide.
 */
function loadGalleryPhotos() {
  for (const image of document.querySelectorAll('media-gallery img[loading="lazy"]')) {
    if (!image.closest('dialog')) image.setAttribute('loading', 'eager');
  }
}

function setupGalleryPhotos() {
  const gallery = document.querySelector('media-gallery');
  if (!gallery) return;
  gallery.addEventListener('pointerdown', loadGalleryPhotos, { once: true, passive: true });
  gallery.addEventListener('scroll', loadGalleryPhotos, { once: true, capture: true, passive: true });
  const later = () => window.setTimeout(loadGalleryPhotos, 1500);
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
}

/* ---------- Start ---------- */

function init() {
  setupSticky();
  syncSize();
  setupAddedSheet();
  setupGalleryPhotos();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
